import { API_ACTIVITY_EVENT } from '../constants/api';
import { authService } from './authService';

/** Event name for rescheduling when the API interceptor has refreshed the token (avoids circular dependency). */
export const TOKEN_REFRESHED_EVENT = 'tokenRefreshed';

/** Dispatched when the session expiring warning should be shown. UI listens and shows Snackbar. */
export const SESSION_EXPIRING_SHOW_EVENT = 'sessionExpiringShow';

/** Dispatched when the session expiring warning should be dismissed (e.g. after reschedule or extend). */
export const SESSION_EXPIRING_DISMISS_EVENT = 'sessionExpiringDismiss';

const INACTIVITY_TIMEOUT_MS = 10 * 60 * 1000;  // 10 minutes of no API requests
const WARNING_BEFORE_MS = 2 * 60 * 1000;       // 2 minutes before logout when warning shown
const REFRESH_BEFORE_MS = 30 * 1000;            // 30 seconds before JWT expiry for refresh
const AUTH_TOKEN_KEY = 'authToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

class SessionRefreshService {
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private inactivityTimer: ReturnType<typeof setTimeout> | null = null;
  private logoutTimer: ReturnType<typeof setTimeout> | null = null;
  private isRefreshing = false;
  private refreshPromise: Promise<boolean> | null = null;
  private tokenRefreshedListenerAttached = false;
  private apiActivityListenerAttached = false;
  private readonly onTokenRefreshed = (): void => this.scheduleFromStorage();
  private readonly onApiActivity = (): void => this.resetInactivity();

  start(): void {
    if (typeof window === 'undefined') return;
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) return;

    if (!this.tokenRefreshedListenerAttached) {
      this.tokenRefreshedListenerAttached = true;
      window.addEventListener(TOKEN_REFRESHED_EVENT, this.onTokenRefreshed);
    }
    if (!this.apiActivityListenerAttached) {
      this.apiActivityListenerAttached = true;
      window.addEventListener(API_ACTIVITY_EVENT, this.onApiActivity);
    }

    this.scheduleFromStorage();
  }

  stop(): void {
    if (typeof window !== 'undefined') {
      if (this.tokenRefreshedListenerAttached) {
        window.removeEventListener(TOKEN_REFRESHED_EVENT, this.onTokenRefreshed);
        this.tokenRefreshedListenerAttached = false;
      }
      if (this.apiActivityListenerAttached) {
        window.removeEventListener(API_ACTIVITY_EVENT, this.onApiActivity);
        this.apiActivityListenerAttached = false;
      }
    }
    this.clearTimersAndWarning();
  }

  /** Clears timers and dismisses warning UI. Does not remove tokenRefreshed listener (used when rescheduling). */
  private clearTimersAndWarning(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
      this.logoutTimer = null;
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRING_DISMISS_EVENT));
    }
  }

  /** Called when API activity fires. Resets inactivity timer so warning only shows after X min of no requests. */
  private resetInactivity(): void {
    if (this.logoutTimer) return; // Warning already shown - user must extend or we'll logout
    this.scheduleInactivityWarning();
  }

  private scheduleFromStorage(): void {
    this.clearTimersAndWarning();

    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) return;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expSeconds = payload.exp as number;
      if (typeof expSeconds !== 'number') return;
      const expiryTime = expSeconds * 1000;
      const now = Date.now();
      const refreshDelay = Math.max(expiryTime - now - REFRESH_BEFORE_MS, 5000);

      this.scheduleInactivityWarning();

      if (refreshDelay > 0) {
        this.refreshTimer = setTimeout(() => {
          this.doRefresh();
        }, refreshDelay);
      }
    } catch {
      // Invalid JWT: no schedule
    }
  }

  private scheduleInactivityWarning(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
    this.inactivityTimer = setTimeout(() => {
      this.inactivityTimer = null;
      this.showSessionWarning();
    }, INACTIVITY_TIMEOUT_MS);
  }

  private showSessionWarning(): void {
    // Cancel automatic refresh – user must click "Extend session". If they don't, they're likely away; log them out.
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.scheduleLogoutAtExpiry();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRING_SHOW_EVENT));
    }
  }

  /** Schedules redirect to login when warning is shown and user does not extend. */
  private scheduleLogoutAtExpiry(): void {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expSeconds = payload.exp as number;
      if (typeof expSeconds !== 'number') return;
      const expiryTime = expSeconds * 1000;
      // Auto-logout after at most 2 minutes from warning; if token expires sooner, logout at expiry.
      const millisUntilExpiry = Math.max(expiryTime - Date.now(), 0);
      const delay = Math.max(Math.min(millisUntilExpiry, WARNING_BEFORE_MS), 1000);
      this.logoutTimer = setTimeout(() => {
        this.logoutTimer = null;
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }, delay);
    } catch {
      // Invalid JWT
    }
  }

  /** Called by UI when user clicks "Extend session". Clears logout timer immediately so the user is not redirected while the refresh is in flight, then refreshes and reschedules. */
  async extendSession(): Promise<boolean> {
    this.clearTimersAndWarning();
    const success = await this.doRefresh();
    if (success) {
      this.scheduleFromStorage();
    }
    return success;
  }

  private async doRefresh(): Promise<boolean> {
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }
    this.isRefreshing = true;
    this.refreshPromise = this.performRefresh();
    try {
      return await this.refreshPromise;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async performRefresh(): Promise<boolean> {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) return false;

    try {
      const response = await authService.refreshToken(refreshToken);
      localStorage.setItem(AUTH_TOKEN_KEY, response.token);
      if (response.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
      }
      this.clearTimersAndWarning();
      this.scheduleFromStorage();
      return true;
    } catch {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      return false;
    }
  }
}

const sessionRefreshService = new SessionRefreshService();

if (typeof window !== 'undefined') {
  (window as unknown as { sessionRefreshService: SessionRefreshService }).sessionRefreshService = sessionRefreshService;
}

export default sessionRefreshService;
